import trapFocus from '../../utils/trapFocus';
import initAtcPopup from '../../components/atc-popup';
import { debounce } from 'throttle-debounce';
import { SETTINGS, CLASSES } from '../../utils/constants';

const initCollectionSearchFilters = () => {
  const filtersContainer = document.querySelector('[data-filters-container]');
  if (!filtersContainer) return;

  const productGridEl = document.querySelector('[data-product-grid]');
  const paginationEl = document.querySelector('[data-pagination]');
  const filterDrawer = filtersContainer.querySelector('[data-filter-drawer]');
  const filterDrawerHeader = filtersContainer.querySelector('[data-filter-drawer-header]');
  const openDrawerBtn = filtersContainer.querySelector('[data-open-filter-drawer]');
  const closeDrawerBtn = filtersContainer.querySelector('[data-close-filter-drawer-button]');
  const filterFormEl = filtersContainer.querySelector('[data-filter-form]');
  const filterDrawerSubmitBtn = filtersContainer.querySelector('[data-filters-submit]');  
  const filterDrawerClearAllBtn = filtersContainer.querySelector('[data-filters-clear-all]');  
  const sortByEl = filtersContainer.querySelector('[data-sort-by-select]');
  const resultsCount = filtersContainer.querySelector('[data-filters-results-count]');
  const activeFiltersContainerEl = filtersContainer.querySelector('[data-active-filters]');
  const activeFilterCount = filtersContainer.querySelector('[data-active-filter-count]');

  const sectionId = filterDrawer?.dataset.sectionId;

  const fetchResults = async (url) => {
    try {
      const requestUrl = new URL(url, window.location.origin);

      if (sectionId) {
        requestUrl.searchParams.set('section_id', sectionId);
      }

      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      console.error('Error fetching filter results:', err);
    }
  };

  const renderProductGrid = (resultsDoc) => {
    if (!resultsDoc) return;

    const mappings = [
      { oldEl: productGridEl, newEl: '[data-product-grid]' },
      { oldEl: paginationEl, newEl: '[data-pagination]' },
      { oldEl: resultsCount, newEl: '[data-filters-results-count]' },
      { oldEl: activeFilterCount, newEl: '[data-active-filter-count]' },
    ];
    
    mappings.forEach(({ oldEl, newEl }) => {
      const newContent = resultsDoc.querySelector(newEl);
      if (oldEl && newContent) oldEl.innerHTML = newContent.innerHTML;
    });

    initAtcPopup();
    if (typeof window.ReloadSmartWishlist === 'function') {
      ReloadSmartWishlist();
    }
  };

  const renderFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newActiveFiltersEl = resultsDoc.querySelector('[data-active-filters]');
    if (newActiveFiltersEl && activeFiltersContainerEl) {
      activeFiltersContainerEl.innerHTML = newActiveFiltersEl.innerHTML;
    }

    const newFiltersSubmitBtn = resultsDoc.querySelector('[data-filters-submit]');
    if (newFiltersSubmitBtn) {
      filterDrawerSubmitBtn.innerHTML = newFiltersSubmitBtn.innerHTML;
    }

    const currentDrawerActiveFiltersEl = filtersContainer.querySelector('[data-drawer-active-filters]');
    const newDrawerActiveFiltersEl = resultsDoc.querySelector('[data-drawer-active-filters]');

    if (newDrawerActiveFiltersEl && filterDrawerHeader) {
      if (currentDrawerActiveFiltersEl) {
        currentDrawerActiveFiltersEl.outerHTML = newDrawerActiveFiltersEl.outerHTML;
      } else {
        filterDrawerHeader.insertAdjacentHTML('beforeend', newDrawerActiveFiltersEl.outerHTML);
      }
    } else if (currentDrawerActiveFiltersEl) {
      currentDrawerActiveFiltersEl.remove();
    }

    const newClearAllBtn = resultsDoc.querySelector('[data-filters-clear-all]');
    if (newClearAllBtn && filterDrawerClearAllBtn) {
      filterDrawerClearAllBtn.href = newClearAllBtn.href;
    }

    const newSortByEl = resultsDoc.querySelector('[data-sort-by-select]');
    if (newSortByEl && sortByEl) {
      sortByEl.value = newSortByEl.value;
    }

    const sortByLabel = filtersContainer.querySelector('[data-sort-by-label]');
    const newSortByLabel = resultsDoc.querySelector('[data-sort-by-label]');
    if (sortByLabel && newSortByLabel) {
      sortByLabel.innerHTML = newSortByLabel.innerHTML;
    }

    const sortOptionsList = filtersContainer.querySelector('[data-sort-by-select-options]');
    const newSortOptionsList = resultsDoc.querySelector('[data-sort-by-select-options]');
    if (sortOptionsList && newSortOptionsList) {
      sortOptionsList.innerHTML = newSortOptionsList.innerHTML;
    }

    const newFiltersFormEl = resultsDoc.querySelector('[data-filter-form]');
    if (newFiltersFormEl && filterFormEl) {
      filterFormEl.innerHTML = newFiltersFormEl.innerHTML;
    }
  };

  const renderPage = async (url) => {
    try {
      const resultsDoc = await fetchResults(url);
      if (!resultsDoc) return;

      renderProductGrid(resultsDoc);
      renderFilters(resultsDoc);
    }
    catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  const handleFilterLinkClick = async (e) => {
    e.preventDefault();
    const closestLink = e.target.closest('a');
    if (!closestLink || !closestLink.href) return;
    
    const urlHref = closestLink.href;
    window.history.pushState({}, '', urlHref);
    await renderPage(urlHref);
  };

  const setUpActiveFilterEventListeners = () => {
    if (!activeFiltersContainerEl) return;

    activeFiltersContainerEl.addEventListener('click', (e) => {
      const activeFilterEl = e.target.closest('[data-active-filter]');
      if (activeFilterEl) {
        handleFilterLinkClick(e);
      }
    });
  };

  const handleDrawerActiveFilterClick = (e) => {
    const activeFilterEl = e.target.closest('[data-active-filter]');
    if (activeFilterEl) {
      handleFilterLinkClick(e);
    }
  };

  const convertFormDataToUrl = () => {
    const formData = new FormData(filterFormEl);
    if (sortByEl) {
      formData.set('sort_by', sortByEl.value);
    }
    const params = new URLSearchParams(formData).toString();
    const url = new URL(window.location.href);
    url.search = params;
    return url;
  };

  const handleFormInput = async () => {
    const url = convertFormDataToUrl();
    if (!url) return;

    window.history.pushState({}, '', url);
    renderPage(url.href);
  };

  const debouncedFormInput = debounce(SETTINGS.FILTER_UPDATE_DELAY, handleFormInput);

  const closeFilterDrawer = () => {
    filterDrawer.classList.remove(CLASSES.TRANSLATE_X_0);
    filterDrawer.classList.add(CLASSES.TRANSLATE_X_100);
    setTimeout(() => {
      filterDrawer.close();
    }, SETTINGS.FILTER_DRAW_ANIMATION_TIME);
  };

  const handleCloseFilterDrawerClick = () => {
    closeFilterDrawer();
  };

  const handleFilterDrawerClick = e => {
    if (!filterDrawer) return;
    const isInDialog = filterDrawer.getBoundingClientRect().right > e.clientX;
    if (!isInDialog) {
      closeFilterDrawer();
    }
  };

  const openFilterDrawer = () => {
    // Force right-edge positioning before showModal() to prevent browser UA centering
    filterDrawer.style.cssText += '; position: fixed !important; top: 0 !important; right: 0 !important; bottom: 0 !important; left: auto !important; margin: 0 !important;';
    filterDrawer.showModal();
    //uses requestAnimationFrame to ensure the class is added after the modal is displayed (on safari)
    //without this, the transition does not work correctly on Safari
    requestAnimationFrame(() => {
      filterDrawer.classList.remove(CLASSES.TRANSLATE_X_100);
      filterDrawer.classList.add(CLASSES.TRANSLATE_X_0);
    });
    trapFocus(filterDrawer);
    filterDrawer.addEventListener('click', handleFilterDrawerClick);
    filterFormEl.addEventListener('input', debouncedFormInput);
    filterFormEl.addEventListener('click', handleDrawerActiveFilterClick);
    filterDrawerSubmitBtn.addEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn.addEventListener('click', handleFilterLinkClick);
    closeDrawerBtn.addEventListener('click', handleCloseFilterDrawerClick);
  };

  openDrawerBtn?.addEventListener('click', () => openFilterDrawer());

  sortByEl?.addEventListener('change', handleFormInput);
  setUpActiveFilterEventListeners();

  filterDrawer?.addEventListener('close', () => {
    filterDrawer.removeEventListener('click', handleFilterDrawerClick);
    filterFormEl.removeEventListener('input', debouncedFormInput);
    filterFormEl.removeEventListener('click', handleDrawerActiveFilterClick);
    filterDrawerSubmitBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    closeDrawerBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn.removeEventListener('click', handleFilterLinkClick);
    openDrawerBtn?.focus();
  });
};

export default initCollectionSearchFilters;